import { ProfileRoleMap } from "@qendo/profile";
import { router, Stack } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { RefreshControl, ScrollView, StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { LeaveTeam } from "~/components/form/team/LeaveTeam";
import { Avatar } from "~/components/layout/Avatar";
import { Columns } from "~/components/layout/Columns";
import { Page } from "~/components/layout/Page";
import { Title } from "~/components/layout/text";
import { Gap } from "~/constants/layout";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useUser } from "~/providers/UserProvider";
import { filled } from "~/util";

export default function Teams() {
    const { profile_id, changeProfile } = useProfile();
    const { teams, isPrimaryTeam, refreshTeams } = useUser();
    const { addToast } = useFeedback();
    const [team, setTeam] = useState<string | null>(null);
    const [leaving, setLeaving] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const current_team = teams.find((t) => t.profile_id === profile_id)?.id ?? null;
    const selected_team = teams.find(({ id }) => id === team);
    const can_leave = filled(selected_team) && !isPrimaryTeam(selected_team.profile_id);
    const can_change = filled(selected_team) && selected_team.profile_id !== profile_id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(current_team): Selected team
    useEffect(() => {
        setTeam(current_team);
        setLeaving(false);
        setRefreshing(false);
    }, []);

    useEffect(() => {
        if (team && !teams.find(({ id }) => id === team)) {
            setTeam(current_team);
        }
    }, [team, teams, current_team]);

    const doRefresh = () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);

        refreshTeams().finally(() => setRefreshing(false));
    };

    const change = () => {
        if (!can_change) {
            return;
        }

        changeProfile(selected_team.profile_id);

        addToast({
            content: isPrimaryTeam(selected_team.profile_id)
                ? "Returned to your profile"
                : `Switched to ${selected_team.name}'s profile`,
        });

        router.dismissTo("/dashboard");
    };

    const leave = () => {
        setLeaving(can_leave);
    };

    const items = useMemo(
        () =>
            teams.map((team) => ({
                value: team.id,
                label: (
                    <View style={styles.team}>
                        <Avatar target={team} size={54} />
                        <View style={Gap.xs}>
                            <Title size="md">{team.name}</Title>
                            {isPrimaryTeam(team.profile_id) ? (
                                <Badge title="Your profile" size="sm" variant="info" />
                            ) : (
                                <Badge title={ProfileRoleMap[team.team_role]} size="sm" />
                            )}
                        </View>
                    </View>
                ),
            })),
        [teams, isPrimaryTeam],
    );

    return (
        <>
            <Stack.Screen options={{ title: "Teams" }} />
            <Page spacer scrollable={false}>
                <ScrollView
                    style={{ flex: 1 }}
                    refreshControl={<RefreshControl refreshing={refreshing} onRefresh={doRefresh} />}
                >
                    <RadioSelector options={items} value={team} itemStyle={styles.select} onValueChange={setTeam} />
                </ScrollView>
                <Columns
                    items={[
                        () => <Button variant="destructive" disabled={!can_leave} title="Leave team" onPress={leave} />,
                        () => <Button variant="primary" disabled={!can_change} title="Change team" onPress={change} />,
                    ]}
                />
            </Page>
            {leaving && can_leave && <LeaveTeam team={selected_team} onRequestClose={() => setLeaving(false)} />}
        </>
    );
}

const styles = StyleSheet.create({
    team: {
        flexDirection: "row",
        gap: 8,
        flexGrow: 1,
    },
    select: {
        flexDirection: "row-reverse",
    },
});
